import { faCalendarDays, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Chip, Grid, TextField, Typography } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
// import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import React, { useState, useCallback, useMemo, useEffect } from "react";
// import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";

import { CustomDate } from "src/models/custom-date/custom-date";

import { intlFormatDateShort } from "src/utils/time-utils";
import { Props, State, DayQuickFilter, DayOptionValue } from "./interfaces";
import { QuickFilterOption } from "../../reusable-components/QuickFilter/interfaces";
import QuickFilter from "../../reusable-components/QuickFilter/QuickFilter";
// import { DatePickerWrapper } from "../FilterByYear/styles";
// import { CustomButton } from "../FilterByYearAndMonth/CustomButton";
import { Button } from "../FilterByYearAndMonth/styles";

export const FilterByDays = <Q extends DayQuickFilter>({
  quickFilters,
  handleChangeQuickFilters,
  filterColor,
  withMaxDate = true,
  disabled = false,
  maxSelectableDays = 31,
  shrinkedLabel,
}: Props<Q>) => {
  const { t } = useTranslation();
  const tRP = "components.quickfilters.days";

  const currDate = useMemo(() => new Date(), []);
  const maxDate = useMemo(() => new Date(currDate.getFullYear() + 1, 11, 31), [currDate]);
  // const maxYear = useMemo(() => currDate.getFullYear() + 1, [currDate]);

  // Internal state for calendar navigation (year/month) - commented out for now
  // const [year, setYear] = useState<number>(currDate.getFullYear());
  // const [month, setMonth] = useState<number>(currDate.getMonth());

  // Handler for year/month navigation - commented out for now
  // const handleChangeYearAndMonth = useCallback((effect: "default" | "increase" | "decrease", date?: Date) => {
  //   if (effect === "increase") {
  //     setMonth((prevMonth) => {
  //       if (prevMonth === 11) {
  //         setYear((prevYear) => prevYear + 1);
  //         return 0;
  //       }
  //       return prevMonth + 1;
  //     });
  //   } else if (effect === "decrease") {
  //     setMonth((prevMonth) => {
  //       if (prevMonth === 0) {
  //         setYear((prevYear) => prevYear - 1);
  //         return 11;
  //       }
  //       return prevMonth - 1;
  //     });
  //   } else if (effect === "default" && date) {
  //     setYear(date.getFullYear());
  //     setMonth(date.getMonth());
  //   }
  // }, []);

  // Internal state for selected days (similar to legalEntities in LegalEntityQuickfilter)
  const [selectedDays, setSelectedDays] = useState<CustomDate[]>([]);

  // Sync internal state with quickFilters (from hook)
  useEffect(() => {
    const daysFromQuickFilter = quickFilters.days?.selectedDays || [];
    setSelectedDays(daysFromQuickFilter);
  }, [quickFilters.days]);

  // Options state (similar to LegalEntityQuickfilter)
  const [options, setOptions] = useState<State>({
    customOptions: [],
    computedOptions: [],
  });

  // Update options when selectedDays change
  useEffect(() => {
    const newOptions: QuickFilterOption<DayOptionValue>[] = selectedDays.map((day) => ({
      name: `${day.day}/${day.month + 1}/${day.year}`,
      value: { selectedDays: [day] },
      code: `${day.day}/${day.month + 1}`,
    }));

    setOptions((state) => ({ ...state, computedOptions: newOptions }));
  }, [selectedDays]);

  const handleAddDay = useCallback(
    (date: Date) => {
      const customDate = CustomDate.FromDate(date);
      const exists = selectedDays.some(
        (day) => day.year === customDate.year && day.month === customDate.month && day.day === customDate.day
      );
      if (!exists && selectedDays.length < maxSelectableDays) {
        const newDays = [...selectedDays, customDate];
        handleChangeQuickFilters("days" as keyof Q, { selectedDays: newDays }, newDays);
      }
    },
    [selectedDays, maxSelectableDays, handleChangeQuickFilters]
  );

  const handleRemoveDay = useCallback(
    (customDate: CustomDate) => {
      const newDays = selectedDays.filter(
        (day) => !(day.year === customDate.year && day.month === customDate.month && day.day === customDate.day)
      );
      handleChangeQuickFilters("days" as keyof Q, { selectedDays: newDays }, newDays);
    },
    [selectedDays, handleChangeQuickFilters]
  );

  const handleClearDays = useCallback(() => {
    handleChangeQuickFilters("days" as keyof Q, { selectedDays: [] }, []);
  }, [handleChangeQuickFilters]);

  const isDaySelected = useCallback(
    (date: Date): boolean => {
      const customDate = CustomDate.FromDate(date);
      return selectedDays.some(
        (day) => day.year === customDate.year && day.month === customDate.month && day.day === customDate.day
      );
    },
    [selectedDays]
  );

  const handleDateSelect = useCallback(
    (date: Date | null) => {
      if (!date) return;

      const customDate = CustomDate.FromDate(date);
      if (isDaySelected(date)) {
        handleRemoveDay(customDate);
      } else {
        handleAddDay(date);
      }
    },
    [isDaySelected, handleRemoveDay, handleAddDay]
  );

  // MUI DesktopDatePicker handler with unknown type
  const handleMUIDateSelect = useCallback(
    (value: unknown) => {
      if (value instanceof Date) {
        handleDateSelect(value);
      }
    },
    [handleDateSelect]
  );

  const getLabel = useCallback(() => {
    if (selectedDays.length === 0) return t(`${tRP}.no_days_selected`, { fallback: "No days selected" });
    if (selectedDays.length === 1) {
      const day = selectedDays[0];
      return `${day.day}/${day.month + 1}/${day.year}`;
    }
    return t(`${tRP}.days_selected`, {
      count: selectedDays.length,
      fallback: `${selectedDays.length} days selected`,
    });
  }, [selectedDays, t, tRP]);

  return (
    <QuickFilter
      options={[...options.customOptions, ...options.computedOptions]}
      quickFilters={quickFilters}
      quickFilter="days"
      handleChangeQuickFilter={handleChangeQuickFilters}
      shrinkedLabel={shrinkedLabel || t(`${tRP}.days`, { fallback: "Days" })}
      customWidth="220px"
      defaultLabel={getLabel()}
    >
      <style>
        {`
    div > p:first-child {
      display: none !important;
    }
    .MuiButtonBase-root.MuiMenuItem-root {
      display: none !important;
    }
        `}
      </style>
      <div style={{ padding: "12px" }}>
        {/* Year/Month Navigation */}
        {/* <Container style={{ marginBottom: "16px" }}>
          <Button
            onClick={() => handleChangeYearAndMonth("decrease")}
            filterColor={filterColor}
            style={{ minWidth: "unset" }}
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faCaretLeft} />
          </Button>
          <DatePickerWrapper>
            <DatePicker
              selected={new Date(year, month, 1)}
              onChange={(date: Date) => handleChangeYearAndMonth("default", date)}
              customInput={createElement(CustomButton)}
              showMonthYearPicker
              dateFormat="MM/yyyy"
              maxDate={withMaxDate ? maxDate : null}
              disabled={disabled}
            />
          </DatePickerWrapper>
          <Button
            onClick={() => handleChangeYearAndMonth("increase")}
            disabled={(year >= maxYear && month === 11) || disabled}
            filterColor={filterColor}
            style={{ minWidth: "unset" }}
          >
            <FontAwesomeIcon icon={faCaretRight} />
          </Button>
        </Container> */}

        <Grid style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <FontAwesomeIcon icon={faCalendarDays} />
          <Typography variant="body1">
            {t(`${tRP}.select_days_count`, {
              current: selectedDays.length,
              max: maxSelectableDays,
              fallback: `Select Days (${selectedDays.length}/${maxSelectableDays})`,
            })}
          </Typography>
          {selectedDays.length > 0 && (
            <Button
              onClick={handleClearDays}
              filterColor={filterColor}
              style={{ minWidth: "unset", padding: "4px 8px" }}
              disabled={disabled}
            >
              <FontAwesomeIcon
                icon={faTimes}
                size="sm"
              />
            </Button>
          )}
        </Grid>
        {selectedDays.length > 0 && (
          <Grid style={{ display: "flex", flexWrap: "wrap", gap: "4px", maxWidth: "300px", margin: "8px 0" }}>
            {selectedDays
              .sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                if (a.month !== b.month) return a.month - b.month;
                return a.day - b.day;
              })
              .map((day) => (
                <Chip
                  key={`${day.year}-${day.month}-${day.day}`}
                  label={intlFormatDateShort(new Date(day.year, day.month - 1, day.day))}
                  size="small"
                  onDelete={() => handleRemoveDay(day)}
                  disabled={disabled}
                  color="primary"
                  variant="outlined"
                />
              ))}
          </Grid>
        )}

        {/* Day Picker */}
        {/* <DatePickerWrapper>
          <DatePicker
            selected={null}
            onChange={handleDateSelect}
            customInput={createElement(CustomButton)}
            inline
            calendarStartDay={1}
            showMonthDropdown={false}
            showYearDropdown={false}
            maxDate={withMaxDate ? maxDate : null}
            disabled={disabled}
            dayClassName={(date) => {
              if (date.getMonth() !== month || date.getFullYear() !== year) {
                return "react-datepicker__day--outside-month";
              }
              return isDaySelected(date) ? "react-datepicker__day--selected" : "";
            }}
            calendarClassName="days-filter-calendar"
          />
        </DatePickerWrapper> */}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={null}
            onChange={handleMUIDateSelect}
            maxDate={withMaxDate ? maxDate : null}
            disabled={disabled}
            renderDay={(day, _selectedDays, pickersDayProps) => {
              const isSelected = !!day && isDaySelected(day);
              return (
                <PickersDay
                  {...pickersDayProps}
                  selected={isSelected}
                />
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
              />
            )}
          />
        </LocalizationProvider>
      </div>
    </QuickFilter>
  );
};

export default FilterByDays;
